import {
  type FC,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const WIDTH = 380;
const MARGIN = 12;

// A 380px panel fixed to the window at a point (the + it opens from), kept
// on screen: moved up by its own height when it would run off the bottom, and
// scrolling inside when taller than the window. Escape closes it.
export const FloatingPanel: FC<{
  top: number;
  left: number;
  label: string;
  onClose(): void;
  children: ReactNode;
}> = ({ top, left, label, onClose, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const panel = ref.current;
    if (!panel) return;
    setHeight(panel.offsetHeight);
    const observer = new ResizeObserver(() => setHeight(panel.offsetHeight));
    observer.observe(panel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      className="fixed z-40 overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-[0_12px_32px_rgba(15,23,42,0.14),0_2px_6px_rgba(15,23,42,0.06)]"
      style={{
        width: WIDTH,
        top: Math.max(
          MARGIN,
          Math.min(top, window.innerHeight - height - MARGIN),
        ),
        maxHeight: window.innerHeight - 2 * MARGIN,
        left: Math.max(
          MARGIN,
          Math.min(left, window.innerWidth - WIDTH - MARGIN),
        ),
      }}
    >
      {children}
    </div>
  );
};
